import { FormInputType } from '@doss/prisma/types'
import { z } from 'zod'
import { ActionData, actionDataSchema } from './action-data.js'
import {
  FormV2LookupCellOutput,
  formV2LookupCellOutputSchema,
  FormV2TableRowOutput,
  formV2TableRowOutputSchema,
} from './action-trigger.js'
import { isLogicExpression, LogicExpression } from './logic.js'

// Form V2 submission schemas and types - encapsulates the structure of the data submitted through
// the Form API without resolving any computed values like lookups or formulas.
// -------------------------------------------------------------------------------------------------

/**
 * Schema for data submitted within a FormInput that has a lookup column.
 */
export const lookupInputDataSchema = z.object({
  type: z.literal(FormInputType.LOOKUP),

  value: z.custom<ActionData>((value) => actionDataSchema.isValidSync(value)).nullable(),
  formattedValue: z.string().nullable(),

  isOverwritingLookup: z.boolean().optional(),
  // If the form input has a lookup column, then this is the list of cell IDs that were selected
  // by the user to populate the form input.
  lookupCellIds: z.array(z.string()),
})

/**
 * Schema for data submitted within a FormInput that has a formula column.
 */
export const formulaInputDataSchema = z.object({
  type: z.literal(FormInputType.FORMULA),

  value: z.custom<ActionData>((value) => actionDataSchema.isValidSync(value)).nullable(),
  formattedValue: z.string().nullable(),

  isOverwritingFormula: z.boolean().optional(),
})

const signatureInputDataSchema = z.object({
  type: z.literal(FormInputType.SIGNATURE),

  formattedValue: z.string().nullable(),
  value: z.string().optional(),
  shouldLinkUserToSignature: z.boolean().optional(),
})

const fileInputDataSchema = z.object({
  type: z.literal(FormInputType.FILE),

  // Pre-upload: value is Record<string, File> (from UploadFile component).
  // Post-upload: value is ActionData (document ID string or string[]).
  // Runtime validation (actionDataSchema) only runs on the backend, which only sees post-upload values.
  value: z
    .custom<ActionData | Record<string, File>>((value) => actionDataSchema.isValidSync(value))
    .nullable(),
  formattedValue: z.string().nullable(),
})

/**
 * Schema for data submitted within a typical FormInput.
 *
 * `slug` carries the original option `value` (the programmatic identifier) for DROPDOWN,
 * RADIO_BUTTON and CHECKBOXES inputs. `value` itself holds the option's human-readable label —
 * a legacy quirk inherited from Forms V1. WaC prefers `slug` when present; v1 workflows
 * continue reading `value` unchanged.
 */
const baseFormInputDataSchema = z.object({
  type: z
    .nativeEnum(FormInputType)
    .refine(
      (type) =>
        type !== FormInputType.LOOKUP &&
        type !== FormInputType.SIGNATURE &&
        type !== FormInputType.FILE &&
        type !== FormInputType.FORMULA,
    ),

  value: z.custom<ActionData>((value) => actionDataSchema.isValidSync(value)).nullable(),
  formattedValue: z.string().nullable(),
  slug: z
    .custom<ActionData>((value) => actionDataSchema.isValidSync(value))
    .nullable()
    .optional(),
})

/**
 * Schema for data submitted within a single FormInput.
 */
const formInputDataSchema = z.union([
  baseFormInputDataSchema,
  lookupInputDataSchema,
  formulaInputDataSchema,
  signatureInputDataSchema,
  fileInputDataSchema,
])

/**
 * Schema for data submitted within a record of FormInputs, keyed by the FormInput's ID.
 */
const formInputRecordsDataSchema = z.record(z.string(), formInputDataSchema)

/**
 * Schema for data submitted within a single FormInputGroup.
 */
const formInputGroupDataSchema = z.object({
  inputs: formInputRecordsDataSchema,
})

/**
 * Schema for data submitted within a record of FormInputGroups, keyed by the FormInputGroup's ID.
 */
const formInputGroupRecordsDataSchema = z.record(z.string(), z.array(formInputGroupDataSchema))

/**
 * Schema for a single visibility condition result submitted by the frontend.
 */
const visibilityConditionResultSchema = z.object({
  result: z.boolean(),
  resolvedExpression: z
    .custom<LogicExpression>((value) => isLogicExpression(value as Record<string, unknown>))
    .optional(),
})

/**
 * Schema for data submitted through a Form.
 */
export const formV2DataSchema = z.object({
  inputs: formInputRecordsDataSchema,
  inputGroups: formInputGroupRecordsDataSchema,
  visibilityConditionResults: z.record(z.string(), visibilityConditionResultSchema).optional(),
})

/**
 * Data submitted within a single FormInput field through a Form.
 */
export type FormInputData = z.infer<typeof formInputDataSchema>

/**
 * A record of FormInputs submitted through a Form. The record is keyed by the FormInput's ID.
 */
export type FormInputRecordsData = z.infer<typeof formInputRecordsDataSchema>

/**
 * Data submitted within a single FormInputGroup, i.e. a group of related FormInputs.
 */
export type FormInputGroupData = z.infer<typeof formInputGroupDataSchema>

/**
 * A record of FormInputGroups submitted through a Form. The record is keyed by the FormInputGroup's
 * ID.
 */
export type FormInputGroupRecordsData = z.infer<typeof formInputGroupRecordsDataSchema>

/**
 * Data submitted through a Form.
 */
export type FormV2Data = z.infer<typeof formV2DataSchema>

// Form V2 resolved data schemas and types - encapsulates the structure of the data submitted
// through the Form API augmented with resolved values like lookups and formulas.
// -------------------------------------------------------------------------------------------------

/**
 * Schema for data submitted within a single FormInput that has been validated, and auxillary data
 * like lookups and formulas resolved.
 */
const resolvedFormInputDataSchema = z.object({
  value: z.custom<ActionData>((value) => actionDataSchema.isValidSync(value)).nullable(),
  formattedValue: z.string().nullable(),
  slug: z
    .custom<ActionData>((value) => actionDataSchema.isValidSync(value))
    .nullable()
    .optional(),

  isOverwritingLookup: z.boolean().optional(),
  isOverwritingFormula: z.boolean().optional(),
  // If the form input has a lookup column, then this is the list of cells resolved from the
  // cell IDs that were selected by the user.
  tableCells: z
    .array(
      z.custom<FormV2LookupCellOutput>((value) => formV2LookupCellOutputSchema.isValidSync(value)),
    )
    .optional(),
})

/**
 * Schema for data submitted within a record of FormInputs, keyed by the FormInput's ID.
 */
const resolvedFormInputRecordsDataSchema = z.record(z.string(), resolvedFormInputDataSchema)

/**
 * Schema for data submitted within a single FormInputGroup that has been validated, and auxillary
 * data like lookups and formulas resolved.
 */
const resolvedFormInputGroupDataSchema = z.object({
  inputs: resolvedFormInputRecordsDataSchema,
})

/**
 * Schema for data submitted within a record of FormInputGroups, keyed by the FormInputGroup's ID.
 */
const resolvedFormInputGroupRecordsDataSchema = z.record(
  z.string(),
  z.array(resolvedFormInputGroupDataSchema),
)

/**
 * Schema for data resolved from a lookup group. Contains the rows from the lookup table
 * that were referenced by the form inputs in this group.
 */
const resolvedFormLookupGroupDataSchema = z.object({
  lookupRows: z
    .array(z.custom<FormV2TableRowOutput>((value) => formV2TableRowOutputSchema.isValidSync(value)))
    .optional(),
})

/**
 * Schema for a record of resolved lookup groups, keyed by the FormLookupInputGroup's ID.
 */
const resolvedFormLookupGroupRecordsDataSchema = z.record(
  z.string(),
  resolvedFormLookupGroupDataSchema,
)

/**
 * Schema for a single visibility condition result
 */
const resolvedVisibilityConditionResultSchema = z.object({
  result: z.boolean(),
  resolvedExpression: z
    .custom<LogicExpression>((value) => isLogicExpression(value as Record<string, unknown>))
    .optional(),
  name: z.string(),
  slug: z.string(),
  conditionExpressionString: z.string().nullable(),
})

/**
 * Data submitted through a Form that has been validated, and auxillary data like lookups and
 * formulas resolved.
 */
export const resolvedFormV2DataSchema = z.object({
  inputs: resolvedFormInputRecordsDataSchema,
  inputGroups: resolvedFormInputGroupRecordsDataSchema,
  lookupGroups: resolvedFormLookupGroupRecordsDataSchema,
  visibilityConditions: z.record(z.string(), resolvedVisibilityConditionResultSchema).optional(),
})

/**
 * Data submitted within a single FormInput field through a Form that has been validated and
 * auxillary data like lookups and formulas resolved.
 */
export type ResolvedFormInputData = z.infer<typeof resolvedFormInputDataSchema>

/**
 * A record of resolved FormInputs submitted through a Form. The record is keyed by the FormInput's
 * ID.
 */
export type ResolvedFormInputRecordsData = z.infer<typeof resolvedFormInputRecordsDataSchema>

/**
 * Data submitted within a single FormInputGroup, i.e. a group of related FormInputs that has been
 * validated and auxillary data like lookups and formulas resolved.
 */
export type ResolvedFormInputGroupData = z.infer<typeof resolvedFormInputGroupDataSchema>

/**
 * A record of resolved FormInputGroups submitted through a Form. The record is keyed by the
 * FormInputGroup's ID.
 */
export type ResolvedFormInputGroupRecordsData = z.infer<
  typeof resolvedFormInputGroupRecordsDataSchema
>

/**
 * Data resolved from a lookup group. Contains the rows from the lookup table
 * that were referenced by the form inputs in this group.
 */
export type ResolvedFormLookupGroupData = z.infer<typeof resolvedFormLookupGroupDataSchema>

/**
 * A record of resolved lookup groups, keyed by the FormLookupInputGroup's ID.
 */
export type ResolvedFormLookupGroupRecordsData = z.infer<
  typeof resolvedFormLookupGroupRecordsDataSchema
>

/**
 * Data submitted through a Form that has been validated and auxillary data like lookups and
 * formulas resolved.
 */
export type ResolvedFormV2Data = z.infer<typeof resolvedFormV2DataSchema>

/**
 * A single visibility condition result.
 */
export type ResolvedVisibilityConditionResult = z.infer<
  typeof resolvedVisibilityConditionResultSchema
>
